import { mapSDUIActionToButtonProps } from "@faire/sdui/lib/actions/utils";
import { getColor } from "@faire/sdui/lib/getColor";
import { Button } from "@faire/slate/components/button";
import { ISDUIText } from "@faire/web-api--source/indigofair/data/ISDUIText";
import { TextAccessibilityAttribute } from "@faire/web-api--source/indigofair/data/TextAccessibilityAttribute";
import { TextDecorator } from "@faire/web-api--source/indigofair/data/TextDecorator";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import {
  Span,
  Typography,
  TypographyTags,
  TypographyVariants,
} from "@faire/web/slate/Typography";
import { ParagraphVariants } from "@faire/web/slate/Typography/Types";
import React from "react";

export const mapSDUITextToPropsArray = (
  sduiText: ISDUIText
): (
  | React.ComponentProps<typeof Typography>
  | Partial<React.ComponentProps<typeof Button>>
)[] => {
  return sduiText.slate_text_segments.map(
    (segment: ISDUIText.ISDUITextSegment) =>
      segment.text_on_click.length > 0
        ? mapSDUITextSegmentToButtonProps(segment)
        : mapSDUITextSegmentToTypographyProps(segment)
  );
};

const mapSDUITextSegmentToTypographyProps = (
  sduiTextSegment: ISDUIText.ISDUITextSegment
): React.ComponentProps<typeof Typography> => {
  const {
    slate_text: { style, content: slateContent } = {},
    type,
    decorators,
    color,
    text_u_i_element,
    content,
  } = sduiTextSegment;

  const textContent =
    text_u_i_element?.content ?? content ?? slateContent ?? "";

  const textDecorators =
    decorators?.length > 0 ? decorators : style?.decorators;

  const props = {
    id: `sdui-text-segment-typography-${textContent}`,
    color: getColor(color ?? style?.color),
    variant: getTypographyVariant(type ?? style?.type, textDecorators),
    // TODO: Remove trim and client side space once we have Backend update to always return text segments with whitespaces
    children: textContent?.trim(),
  };

  if (
    type === TextStyleType.PARAGRAPH ||
    type === TextStyleType.LABEL ||
    style?.type === TextStyleType.PARAGRAPH ||
    style?.type === TextStyleType.LABEL
  ) {
    return {
      ...props,
      variant: props.variant as ParagraphVariants,
      textDecoration: getTypographyTextDecoration(
        type ?? style?.type,
        textDecorators
      ),
    };
  }

  return {
    ...props,
  };
};

const getTypographyTextDecoration = (
  textStyleType: keyof typeof TextStyleType | undefined,
  textDecorators: Array<keyof typeof TextDecorator> | undefined
) => {
  if (
    textStyleType !== TextStyleType.PARAGRAPH &&
    textStyleType !== TextStyleType.LABEL
  ) {
    return undefined;
  }

  if (textDecorators?.includes(TextDecorator.UNDERLINE)) {
    return "underline";
  }

  if (textDecorators?.includes(TextDecorator.STRIKE_THROUGH)) {
    return "strikethrough";
  }

  return undefined;
};

const mapSDUITextSegmentToButtonProps = (
  sduiTextSegment: ISDUIText.ISDUITextSegment
): Partial<React.ComponentProps<typeof Button>> => {
  const {
    slate_text: { style, content: slateContent } = {},
    type,
    decorators,
    color,
    text_u_i_element,
    content,
  } = sduiTextSegment;

  const actionProps = mapSDUIActionToButtonProps(
    sduiTextSegment.text_on_click ?? []
  );

  const textContent =
    text_u_i_element?.content ?? content ?? slateContent ?? "";

  const textDecorators =
    decorators?.length > 0 ? decorators : style?.decorators;

  return {
    id: `sdui-text-segment-button-${textContent}`,
    variant: "plain",
    plainColor: "inherit",
    children: (
      <Span
        variant={getTypographyVariant(type ?? style?.type, textDecorators)}
        color={getColor(color ?? style?.color)}
      >
        {textContent?.trim()}
      </Span>
    ),
    ...actionProps,
  };
};

export const getTypographyVariant = (
  textStyleType: keyof typeof TextStyleType | undefined,
  textDecorators: Array<keyof typeof TextDecorator> | undefined
): TypographyVariants | undefined => {
  if (textStyleType === undefined) {
    return undefined;
  }
  const hasEmphasis = textDecorators?.includes(TextDecorator.EMPHASIS);
  const hasStrikeThrough = textDecorators?.includes(
    TextDecorator.STRIKE_THROUGH
  );
  const hasUnderline = textDecorators?.includes(TextDecorator.UNDERLINE);

  if (hasEmphasis && textStyleType === TextStyleType.PARAGRAPH) {
    return "paragraphSansMedium";
  }

  if (hasStrikeThrough || hasUnderline) {
    return "paragraphSansRegular";
  }

  if (hasEmphasis) {
    return MEDIUM_VARIANTS[textStyleType] ?? REGULAR_VARIANTS[textStyleType];
  }

  return REGULAR_VARIANTS[textStyleType] ?? MEDIUM_VARIANTS[textStyleType];
};

const MEDIUM_VARIANTS: Record<
  keyof typeof TextStyleType,
  TypographyVariants | undefined
> = {
  [TextStyleType.TEXT_STYLE_TYPE_UNKNOWN]: undefined,
  [TextStyleType.LABEL]: "labelSansMedium",
  [TextStyleType.PARAGRAPH]: "paragraphSansMedium",
  [TextStyleType.SUBHEADING]: "subheadingSansMedium",
  [TextStyleType.SECTION_HEADER]: "sectionHeaderSansMedium",
  [TextStyleType.PAGE_HEADER]: undefined,
  [TextStyleType.DISPLAY_S_SANS]: "displaySSansSemiBold",
  [TextStyleType.DISPLAY_S_SERIF]: undefined,
  [TextStyleType.DISPLAY_M]: undefined,
  [TextStyleType.DISPLAY_L]: undefined,
  [TextStyleType.DISPLAY_XL]: undefined,
};

const REGULAR_VARIANTS: Record<
  keyof typeof TextStyleType,
  TypographyVariants | undefined
> = {
  [TextStyleType.TEXT_STYLE_TYPE_UNKNOWN]: undefined,
  [TextStyleType.LABEL]: "labelSansRegular",
  [TextStyleType.PARAGRAPH]: "paragraphSansRegular",
  [TextStyleType.SUBHEADING]: "subheadingSansRegular",
  [TextStyleType.SECTION_HEADER]: undefined,
  [TextStyleType.PAGE_HEADER]: "pageHeaderSerifRegular",
  [TextStyleType.DISPLAY_S_SANS]: "displaySSansRegular",
  [TextStyleType.DISPLAY_S_SERIF]: "displaySSerifRegular",
  [TextStyleType.DISPLAY_M]: "displayMSerifRegular",
  [TextStyleType.DISPLAY_L]: "displayLSerifRegular",
  [TextStyleType.DISPLAY_XL]: "displayXLSerifRegular",
};

export const accessibilityTextTagMap: {
  [key in keyof typeof TextAccessibilityAttribute]: TypographyTags | undefined;
} = {
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_UNKNOWN]: undefined,
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_H1]: "h1",
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_H2]: "h2",
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_H3]: "h3",
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_H4]: "h4",
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_H5]: "h5",
  [TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_P]: "p",
};
